// Story 8.5 — GET /api/v1/creators/me/payout/options.
//
// Everything the creator payout surface needs in ONE round-trip: the earnings
// pot, the rails this provider actually executes, the flat fee on each, the
// settlement schedule, and whether a request placed right now would succeed.
//
// ⚠ IT CARRIES `earnings_cents` AND NOT `balance_cents`, and the SPA must never
// add one to the other (Story 8.2 AC1). They are two pots with two different
// legal characters: the earnings pot is supplier income that can be paid out,
// the Greenhold balance is a closed-loop licence to spend here. One headline
// figure would imply one redeemable balance, which is exactly the claim the
// whole model exists to deny.
//
// One request rather than three (balance, then destination, then the provider's
// country list) because the intermediate states are misleading: "you have €40
// and no way to be paid" is the wrong thing to flash at a creator while the
// third request is still in the air.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type PayoutRail = "bank" | "card";

/** Three states, and the third one matters. `unknown` means the payout
 *  provider has not told us whether it reaches this country — which is the
 *  truthful answer while no provider is signed. It is NOT "no": telling a
 *  creator in a supported country that they can never be paid would be a lie in
 *  the other direction. */
export type PayoutCountryStatus = "supported" | "unsupported" | "unknown";

export type CreatorPayoutIneligibleReason =
  | "country_unsupported"
  | "country_unknown"
  | "rail_unsupported"
  | "below_minimum"
  | "already_queued_this_period"
  /** Story 8.10 — no legal identity on file, so no settlement voucher can name
   *  the creator. Reachable only for a creator approved before the identity
   *  block was collected; the remedy is the panel's own "Your details" form. */
  | "identity_not_on_file";

/** Story 8.10 — the identity block AS STORED, which is why every field is
 *  nullable. The rows that matter most are the incomplete ones: a creator
 *  approved before the block was collected has an approved application with all
 *  of these null, and the panel's job is to say so and offer the form.
 *
 *  ⛔ `business_registration_number` is a CVR / company registration number. The
 *  panel labels it "Registration number", never "VAT number" — the same label
 *  the settlement voucher prints, and for the same reason. */
export type StoredCreatorIdentity = {
  legal_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  country_code: string | null;
  business_status: "private" | "business" | null;
  business_registration_number: string | null;
};

export type RailOption = {
  rail: PayoutRail;
  /** FLAT, at cost, and disclosed before the creator confirms (Story 1.37). */
  fee_cents: number;
  expected_days: string;
};

export type PayoutDestination = {
  status: "verified" | "pending_verification";
  last4: string | null;
  holder_name: string | null;
  country: string | null;
};

export type CreatorPayoutOptions = {
  earnings_cents: number;
  minimum_cents: number;
  period: { start: string; end: string };
  rails: RailOption[];
  country_status: PayoutCountryStatus;
  destination_country: string | null;
  destination: PayoutDestination | null;
  can_request: boolean;
  ineligible_reason: CreatorPayoutIneligibleReason | null;
  queued_this_period: boolean;
  /** Story 8.10 — its own flag rather than an inference from
   *  `ineligible_reason`, which reports only the FIRST blocker: a creator in an
   *  unsupported country who also has no identity on file would report the
   *  country and say nothing about the identity. */
  counterparty_identity_on_file: boolean;
  /** `null` = not an approved creator. An object with null fields = approved,
   *  and we hold nothing. Two different things, and the panel says different
   *  things about them. */
  counterparty_identity: StoredCreatorIdentity | null;
  /** The free earnings → balance route. Read from the payload rather than
   *  hardcoded in copy, so no surface can quietly stop offering it. */
  conversion_is_free: boolean;
};

export const PAYOUT_OPTIONS_QUERY_KEY = ["creator-payout-options"] as const;

async function fetchPayoutOptions(): Promise<CreatorPayoutOptions> {
  const response = await fetch("/api/v1/creators/me/payout/options", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`payout options fetch failed: ${response.status}`);
  }
  return (await response.json()) as CreatorPayoutOptions;
}

export function usePayoutOptions() {
  return useQuery({
    queryKey: PAYOUT_OPTIONS_QUERY_KEY,
    queryFn: fetchPayoutOptions,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
